/**
 * Redux slice for managing voice mode state
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import logger from '../utils/logger/logger';

export interface VoiceModeState {
  isActive: boolean;
  isLoading: boolean;
  isListening: boolean;
  isPaused: boolean; // true when microphone is paused (not sending audio)
  isRestarting: boolean; // true when restarting microphone after pause
  audioLevel: number; // 0-100 representing the current audio level for visualization
  showInactivityWarning: boolean; // true when showing the inactivity warning dialog
  inactivityCountdown: number; // seconds remaining until voice mode ends (0-60)
  disconnectedByInactivity: boolean; // true when voice mode ended due to inactivity timeout
  showNoAudioDialog: boolean; // true when showing the no-audio-detected dialog
}

const initialState: VoiceModeState = {
  isActive: false,
  isLoading: false,
  isListening: false,
  isPaused: false,
  isRestarting: false,
  audioLevel: 0,
  showInactivityWarning: false,
  inactivityCountdown: 60,
  disconnectedByInactivity: false,
  showNoAudioDialog: false,
};

const voiceModeSlice = createSlice({
  name: 'voiceMode',
  initialState,
  reducers: {
    setVoiceModeActive: (state, action: PayloadAction<boolean>) => {
      logger.log(`[voiceModeSlice] setVoiceModeActive called with: ${action.payload}, current state: ${state.isActive}`);
      state.isActive = action.payload;
      // Reset other states when deactivating
      if (!action.payload) {
        state.isLoading = false;
        state.isListening = false;
        state.isPaused = false;
        state.isRestarting = false;
        state.audioLevel = 0;
        // Don't reset showInactivityWarning - let it persist to show disconnection message
        // state.showInactivityWarning = false;
        state.inactivityCountdown = 60;
        // Don't reset disconnectedByInactivity here - it needs to persist to show the dialog
        logger.log('[voiceModeSlice] Voice mode deactivated - reset all states except inactivity warning');
      } else {
        // Reset disconnectedByInactivity when starting a new voice session
        state.disconnectedByInactivity = false;
      }
    },
    setVoiceModeLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setVoiceModeListening: (state, action: PayloadAction<boolean>) => {
      state.isListening = action.payload;
    },
    setVoiceModePaused: (state, action: PayloadAction<boolean>) => {
      state.isPaused = action.payload;
    },
    setVoiceModeRestarting: (state, action: PayloadAction<boolean>) => {
      state.isRestarting = action.payload;
    },
    setAudioLevel: (state, action: PayloadAction<number>) => {
      state.audioLevel = Math.max(0, Math.min(100, action.payload));
    },
    setShowInactivityWarning: (state, action: PayloadAction<boolean>) => {
      state.showInactivityWarning = action.payload;
      // Reset countdown when showing warning
      if (action.payload) {
        state.inactivityCountdown = 60;
      }
    },
    setInactivityCountdown: (state, action: PayloadAction<number>) => {
      state.inactivityCountdown = Math.max(0, action.payload);
    },
    setDisconnectedByInactivity: (state, action: PayloadAction<boolean>) => {
      state.disconnectedByInactivity = action.payload;
    },
    setShowNoAudioDialog: (state, action: PayloadAction<boolean>) => {
      state.showNoAudioDialog = action.payload;
    },
  },
});

export const { 
  setVoiceModeActive, 
  setVoiceModeLoading, 
  setVoiceModeListening,
  setVoiceModePaused,
  setVoiceModeRestarting,
  setAudioLevel,
  setShowInactivityWarning,
  setInactivityCountdown,
  setDisconnectedByInactivity,
  setShowNoAudioDialog
} = voiceModeSlice.actions;

export const voiceModeSelector = (state: { voiceMode: VoiceModeState }) => state.voiceMode;
export default voiceModeSlice.reducer;
